import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as productService from '../lib/services/productService';
import * as reviewService from '../lib/services/reviewService';
import * as adminService from '../lib/services/adminService';
import { useCustomerType } from '../context/CustomerTypeContext';
import CustomerTypeSelection from '../components/CustomerTypeSelection';
import './Home.css';

/**
 * Home Component - Responsive Landing Page
 * 
 * Features:
 * - Dynamic screen size detection (Mobile: ≤480px, Tablet: 481-1024px, Desktop: >1024px)
 * - Optimized image rendering based on device (loads only visible slides on mobile)
 * - Touch/swipe support for mobile navigation
 * - Lazy loading for non-active slideshow images
 * - Hardware-accelerated animations for smooth transitions
 * - Responsive slideshow timing (6s on mobile, 5s on desktop)
 * - First image preloading for better performance
 */

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewStartIndex, setReviewStartIndex] = useState(0);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [reviews, setReviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 480,
    isTablet: window.innerWidth > 480 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024
  });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();
  const { customerType, isPrivate } = useCustomerType();

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 50;

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 480,
        isTablet: width > 480 && width <= 1024,
        isDesktop: width > 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Dynamically load all images from the slideshow folder
    const loadImages = () => {
      try {
        const context = require.context('../../public/slideshow', false, /\.(jpg|jpeg|png|gif|webp)$/i);
        const images = context.keys().map(key => `/slideshow/${key.replace('./', '')}`);
        setSlideshowImages(images);
        
        // Preload the first image for better performance
        if (images.length > 0) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = images[0];
          document.head.appendChild(link);
        }
      } catch (error) {
        console.error('Error loading slideshow images:', error);
        setSlideshowImages([]);
      }
    };


    loadImages();
    fetchProducts();
    fetchTopReviews();
    fetchOffers();
  }, []);


  const fetchProducts = async () => {
    try {
      const products = await productService.getProducts();

      // Group products by category and take first 2 from each
      const grouped = {};
      products.forEach(product => {
        if (!grouped[product.category]) {
          grouped[product.category] = [];
        }
        if (grouped[product.category].length < 2) {
          grouped[product.category].push(product);
        }
      });
      setCategoryProducts(grouped);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchTopReviews = async () => {
    try {
      const topReviews = await reviewService.getTopReviews(10);
      const safeReviews = Array.isArray(topReviews) ? topReviews : [];
      setReviews(safeReviews.slice(0, 10));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const offerList = await adminService.getPublicOffers();
      setOffers(Array.isArray(offerList) ? offerList : []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setOffers([]);
    }
  };

  const parseDateOnly = (value) => {
    if (!value) return null;
    const safeValue = String(value).split('T')[0];
    const [year, month, day] = safeValue.split('-').map((part) => parseInt(part, 10));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  const formatDateOnly = (value) => {
    const parsedDate = parseDateOnly(value);
    return parsedDate ? parsedDate.toLocaleDateString() : '';
  };

  const formatReviewPostedDate = (value) => {
    const parsedDate = parseDateOnly(value);
    return parsedDate ? parsedDate.toISOString().slice(0, 10) : '';
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const activeOffers = offers.filter((o) => {
    const start = parseDateOnly(o.start_date);
    return !start || start <= today;
  });
  const upcomingOffers = offers.filter((o) => {
    const start = parseDateOnly(o.start_date);
    return start && start > today;
  });
  const allVisibleOffers = [...activeOffers, ...upcomingOffers];

  const renderStars = (stars) => {
    const safeStars = Math.max(1, Math.min(5, Number(stars) || 1));
    return '★'.repeat(safeStars) + '☆'.repeat(5 - safeStars);
  };

  const visibleReviews = reviews.slice(0, 10);
  const reviewsToDisplay = visibleReviews.length > 0
    ? Array.from({ length: Math.min(5, visibleReviews.length) }, (_, index) => {
        const reviewIndex = (reviewStartIndex + index) % visibleReviews.length;
        return visibleReviews[reviewIndex];
      })
    : [];

  const handlePreviousReview = () => {
    if (visibleReviews.length <= 1) {
      return;
    }

    setReviewStartIndex((prevIndex) => (
      prevIndex === 0 ? visibleReviews.length - 1 : prevIndex - 1
    ));
  };

  const handleNextReview = () => {
    if (visibleReviews.length <= 1) {
      return;
    }

    setReviewStartIndex((prevIndex) => (prevIndex + 1) % visibleReviews.length);
  };

  const handleCategoryClick = (category) => {
    navigate('/products', { state: { selectedCategory: category } });
  };

  // Touch handlers for swipe navigation on mobile
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Swipe left - go to next slide
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
    } else if (isRightSwipe) {
      // Swipe right - go to previous slide
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? slideshowImages.length - 1 : prevIndex - 1
      );
    }
  };

  // Determine if an image should be rendered based on screen size
  const shouldRenderImage = useCallback((index) => {
    // On mobile, only render current, previous, and next images for performance
    if (screenSize.isMobile) {
      const prevIndex = currentImageIndex === 0 ? slideshowImages.length - 1 : currentImageIndex - 1;
      const nextIndex = (currentImageIndex + 1) % slideshowImages.length;
      return index === currentImageIndex || index === prevIndex || index === nextIndex;
    }
    // On tablet and desktop, render all images
    return true;
  }, [screenSize.isMobile, currentImageIndex, slideshowImages.length]);

  // Get responsive image sizes based on screen width
  const getImageSizes = () => {
    if (screenSize.isMobile) {
      return '100vw';
    } else if (screenSize.isTablet) {
      return '100vw';
    }
    return '100vw';
  };

  useEffect(() => {
    if (slideshowImages.length > 0) {
      // Adjust slideshow speed based on screen size
      const intervalTime = screenSize.isMobile ? 6000 : 5000; // Slightly slower on mobile
      
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % slideshowImages.length);
      }, intervalTime);


      return () => clearInterval(interval);
    }
  }, [slideshowImages, screenSize.isMobile]);

  useEffect(() => {
    if (visibleReviews.length === 0) {
      if (reviewStartIndex !== 0) {
        setReviewStartIndex(0);
      }
      return;
    }

    if (reviewStartIndex >= visibleReviews.length) {
      setReviewStartIndex(0);
    }
  }, [reviewStartIndex, visibleReviews.length]);


  return (
    <div className="home">
      {!customerType && <CustomerTypeSelection />}
      {/* Hero Section with Slideshow */}
      <section className="hero">
        {slideshowImages.length > 0 ? (
          <div 
            className="slideshow"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {slideshowImages.map((imagePath, index) => {
              const isActive = index === currentImageIndex;
              const shouldRender = shouldRenderImage(index);
              
              // Skip rendering non-visible images on mobile for performance
              if (!shouldRender) return null;

              return (
                <div
                  key={index}
                  className={`slideshow-item ${isActive ? 'active' : ''}`}
                >
                  <img 
                    src={imagePath} 
                    alt={`Slide ${index + 1}`}
                    loading={isActive ? 'eager' : 'lazy'}
                    sizes={getImageSizes()}
                    style={{
                      willChange: isActive ? 'opacity' : 'auto'
                    }}
                    decoding="async"
                  />
                </div>
              );
            })}
            <div className="slideshow-indicators">
              {slideshowImages.map((_, index) => (
                <span
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(index)
                  }}
                  role="button"
                  aria-label={`Go to slide ${index + 1}`}
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setCurrentImageIndex(index);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="no-slideshow-images">
            <p>Add images to the public/slideshow folder to display the banner</p>
          </div>
        )}
      </section>

      {isPrivate && allVisibleOffers.length > 0 && (
        <section className="private-offers-section">
          <div className="container">
            <div className="private-offers-grid">
              {allVisibleOffers.map((offer) => {
                const isUpcoming = upcomingOffers.includes(offer);
                return (
                  <article className={`private-offer-card${isUpcoming ? ' offer-upcoming' : ''}`} key={offer.id}>
                    <p className={`private-offer-chip${isUpcoming ? ' chip-upcoming' : ''}`}>{isUpcoming ? 'UPCOMING' : 'OFFER'}</p>
                    <h3>{offer.title || `${offer.category} Offer`}</h3>
                    {isUpcoming && offer.start_date && (
                      <p className="offer-start-date">Starts {formatDateOnly(offer.start_date)}</p>
                    )}
                    {String(offer.offer_type || 'bundle').toLowerCase() === 'discount' ? (
                      <>
                        <p className="private-offer-rule">
                          Enjoy {Number(offer.discount_percent || 0).toFixed(2)}% on all {offer.category} items with every order.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="private-offer-rule">
                          Buy any {offer.required_quantity} {offer.category} items for just {offer.currency || 'SEK'} {Number(offer.offer_price || 0).toFixed(2)}.
                        </p>
                      </>
                    )}
                    {offer.end_date && (
                      <p className="offer-end-date">Valid until {formatDateOnly(offer.end_date)}</p>
                    )}
                    {offer.description && (
                      <p className="private-offer-note">{offer.description}</p>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}


      {/* Products Section */}
      <section className="section products-section">
        <div className="container">
          <h2 className="section-title">Our Products</h2>
          <p className="section-subtitle">
            A tasty blend of tradition and health. Light, crispy and the perfect health-friendly snack!
          </p>

          <div className="categories-grid">
            {Object.entries(categoryProducts).map(([category, products]) => (
              <div key={category} className="category-block">
                <div className="category-header">
                  <h3>{category}</h3>
                  <button 
                    onClick={() => handleCategoryClick(category)}
                    className="btn-view-category"
                  >
                    View More →
                  </button>
                </div>
                <div className="category-products">
                  {products.map(product => (
                    <Link 
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="product-card-mini"
                    >
                      <div className="product-image-mini">
                        {product.image_url && (
                          <img src={product.image_url} alt={product.name} />
                        )}
                        {product.gluten_free && (
                          <span className="gluten-free-tag-mini">Gluten Free</span>
                        )}
                      </div>
                      <div className="product-info-mini">
                        <h4>{product.name}</h4>
                        {product.weight && product.weight > 0 && (
                          <p style={{ fontSize: '0.8em', color: '#666', margin: '3px 0' }}>📦 {product.weight}g</p>
                        )}
                        {isPrivate && (
                          <>
                            {product.discounted_price && product.discounted_price < product.price ? (
                              <div className="price-mini">
                                <span className="old-price-mini">kr {Number(product.price).toFixed(2)}</span>
                                <span className="new-price-mini">kr {Number(product.discounted_price).toFixed(2)}</span>
                              </div>
                            ) : (
                              <p className="price-mini">kr {Number(product.price).toFixed(2)}</p>
                            )}
                          </>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/products" className="btn btn-outline">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      <section className="section feedback-section">
        <div className="container">
          <h2 className="section-title">Customer Feedback</h2>
          <p className="section-subtitle">
            Here is what our customers are saying.
          </p>
        </div>

        <div className="feedback-list-wrapper">
          {reviewsToDisplay.length > 0 ? (
            <div className="feedback-carousel">
              <button
                type="button"
                className="feedback-nav-button"
                onClick={handlePreviousReview}
                aria-label="Show previous review"
                disabled={visibleReviews.length <= 1}
              >
                &#8249;
              </button>

              <div className="feedback-card-grid">
                {reviewsToDisplay.map((review, index) => (
                  <article className="feedback-card" key={`${review.id || 'review'}-${reviewStartIndex}-${index}`}>
                    <div className="feedback-stars">{renderStars(review.stars)}</div>
                    {review.category ? (
                      <p className="feedback-product">Category: {review.category}</p>
                    ) : review.product_name ? (
                      <p className="feedback-product">{review.product_name}</p>
                    ) : (
                      <p className="feedback-product">All Categories</p>
                    )}
                    <p className="feedback-comment">"{review.review_comments}"</p>
                    <div className="feedback-meta">
                      <span className="feedback-name">{review.first_name}</span>
                      <span className="feedback-location">{review.location}</span>
                      {review.created_at && (
                        <span className="feedback-date">Posted on: {formatReviewPostedDate(review.created_at)}</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>

              <button
                type="button"
                className="feedback-nav-button"
                onClick={handleNextReview}
                aria-label="Show next review"
                disabled={visibleReviews.length <= 1}
              >
                &#8250;
              </button>
            </div>
          ) : (
            <div className="feedback-empty-state">Be the first to leave feedback.</div>
          )}
        </div>
      </section>
    </div>
  );
};


export default Home;